/**
 * HD - Sizefinder
 *
 * Lets the customer pick their head circumference (cm) and recommends the matching hat size.
 *
 * The widget auto-detects how the product's size option is labelled:
 *   - Numeric sizes (e.g. "55", "57"): the entered cm value is used directly as the size, the
 *     letter (XS-XXL) from the mapping table is shown as extra info.
 *   - Letter sizes (e.g. "XS", "M", "XXL"): the entered cm value is mapped to a letter via the
 *     mapping table and the matching letter variant is selected.
 *
 * On "apply", the matching product variant is selected by checking the related variant-picker radio
 * and dispatching a change event, which the theme's VariantPicker reacts to (updating price,
 * availability and the add-to-cart button).
 */
(function () {
  if (window.customElements.get('hd-sizefinder')) {
    return;
  }

  var LETTER_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '3XL', '4XL'];

  /** Extract the first integer found in a string (e.g. "Größe 57" -> 57, "57 cm" -> 57). */
  function extractNumber(value) {
    if (value === null || value === undefined) return null;
    var match = String(value).match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
  }

  function normalizeLetter(value) {
    return String(value === null || value === undefined ? '' : value)
      .trim()
      .toUpperCase();
  }

  class HdSizefinder extends HTMLElement {
    connectedCallback() {
      this.formId = this.getAttribute('form-id');
      this.optionPosition = this.getAttribute('option-position');
      this.min = parseInt(this.getAttribute('min'), 10) || 52;
      this.max = parseInt(this.getAttribute('max'), 10) || 63;

      this.range = this.querySelector('[data-hd-range]');
      this.bubble = this.querySelector('[data-hd-bubble]');
      this.ticksEl = this.querySelector('[data-hd-ticks]');
      this.resultSizeEl = this.querySelector('[data-hd-result-size]');
      this.fitEl = this.querySelector('[data-hd-fit]');
      this.fitTextEl = this.querySelector('[data-hd-fit-text]');
      this.hintEl = this.querySelector('[data-hd-hint]');

      this.letterMap = this.parseMapping(this.getAttribute('size-mapping'));
      this.entries = this.parseSizes();
      this.isNumeric = this.entries.some(function (entry) {
        return extractNumber(entry.name) !== null;
      });
      this.assignRanks();

      this.buildTicks();

      if (this.range) {
        // input -> live visual feedback; change -> commit the matching variant selection.
        this.range.addEventListener('input', this.update.bind(this));
        this.range.addEventListener('change', this.apply.bind(this));
      }

      this.update();
    }

    /** Parse the "cm:Letter" mapping textarea into a lookup object keyed by cm. */
    parseMapping(raw) {
      var map = {};
      if (!raw) return map;

      raw.split(/\r?\n/).forEach(function (line) {
        var parts = line.split(':');
        if (parts.length >= 2) {
          var cm = extractNumber(parts[0]);
          var letter = parts.slice(1).join(':').trim();
          if (cm !== null && letter) {
            map[cm] = letter;
          }
        }
      });

      return map;
    }

    /** Parse the embedded variant size data. */
    parseSizes() {
      var script = this.querySelector('script[data-hd-sizes]');
      if (!script) return [];

      try {
        return JSON.parse(script.textContent) || [];
      } catch (error) {
        return [];
      }
    }

    /** Give every entry a numeric rank so we can compare sizes regardless of numeric/letter labelling. */
    assignRanks() {
      var self = this;
      this.entries.forEach(function (entry) {
        if (self.isNumeric) {
          entry.rank = extractNumber(entry.name);
        } else {
          var index = LETTER_ORDER.indexOf(normalizeLetter(entry.name));
          entry.rank = index === -1 ? null : index;
        }
      });
    }

    /** The desired rank for a given head circumference, matching the entry ranks. */
    desiredRank(cm) {
      if (this.isNumeric) return cm;
      var letter = this.letterMap[cm];
      if (!letter) return null;
      var index = LETTER_ORDER.indexOf(normalizeLetter(letter));
      return index === -1 ? null : index;
    }

    /** The entry that exactly matches the recommendation, if the product offers that size at all. */
    resolveTarget(cm) {
      var rank = this.desiredRank(cm);
      if (rank === null) return null;

      for (var i = 0; i < this.entries.length; i += 1) {
        if (this.entries[i].rank === rank) return this.entries[i];
      }
      return null;
    }

    buildTicks() {
      if (!this.ticksEl) return;

      var html = '';
      for (var cm = this.min; cm <= this.max; cm += 1) {
        var target = this.resolveTarget(cm);
        var unavailableClass = target && target.available ? '' : ' class="is-unavailable"';
        html += '<span data-tick="' + cm + '"' + unavailableClass + '>' + cm + '</span>';
      }
      this.ticksEl.innerHTML = html;
    }

    get currentCm() {
      return parseInt(this.range ? this.range.value : this.min, 10);
    }

    update() {
      var cm = this.currentCm;
      var letter = this.letterMap[cm];
      var target = this.resolveTarget(cm);
      var available = !!(target && target.available);

      this.positionBubble(cm);
      this.highlightTick(cm);

      if (this.bubble) {
        this.bubble.textContent = cm;
      }

      if (this.resultSizeEl) {
        var label;
        if (this.isNumeric) {
          label = 'Größe ' + cm + (letter ? ' \u00b7 ' + letter : '');
        } else {
          label = letter ? 'Größe ' + letter : 'Größe \u2013';
        }
        this.resultSizeEl.textContent = label;
      }

      this.updateFit(cm, target, available);
    }

    positionBubble(cm) {
      if (!this.bubble || !this.range) return;

      var ratio = (cm - this.min) / (this.max - this.min || 1);
      // Account for the 18px thumb so the bubble stays centred over it.
      var offset = 9 - ratio * 18;
      this.bubble.style.left = 'calc(' + ratio * 100 + '% + ' + offset + 'px)';
    }

    highlightTick(cm) {
      if (!this.ticksEl) return;
      this.ticksEl.querySelectorAll('span').forEach(function (span) {
        span.classList.toggle('is-active', parseInt(span.getAttribute('data-tick'), 10) === cm);
      });
    }

    updateFit(cm, target, available) {
      if (!this.fitEl) return;

      if (!target) {
        this.fitEl.classList.add('is-unavailable');
        if (this.fitTextEl) this.fitTextEl.textContent = 'Nicht verfügbar';
        this.showUnavailableHint(cm);
        return;
      }

      if (!available) {
        this.fitEl.classList.add('is-unavailable');
        if (this.fitTextEl) this.fitTextEl.textContent = 'Ausverkauft';
        this.showUnavailableHint(cm);
        return;
      }

      this.fitEl.classList.remove('is-unavailable');
      if (this.fitTextEl) this.fitTextEl.textContent = 'Normaler Sitz';
      this.showLooserHint(target);
    }

    /** Suggest sizing up for a looser fit when the next bigger size exists and is available. */
    showLooserHint(target) {
      if (!this.hintEl) return;

      var larger = this.nextAvailableAbove(target.rank);
      if (larger) {
        this.hintEl.textContent = 'Wenn du es lockerer magst, nimm Größe ' + larger.name + '.';
      } else {
        this.hintEl.textContent = '';
      }
    }

    /** Point the customer to the nearest available size when the chosen one is missing. */
    showUnavailableHint(cm) {
      if (!this.hintEl) return;

      var rank = this.desiredRank(cm);
      var nearest = rank === null ? this.firstAvailable() : this.nearestAvailable(rank);

      if (nearest) {
        this.hintEl.textContent = 'Nicht verfügbar. Nächste Größe: ' + nearest.name + '.';
      } else {
        this.hintEl.textContent = 'Diese Größe ist leider nicht verfügbar.';
      }
    }

    nextAvailableAbove(rank) {
      if (rank === null) return null;
      var best = null;
      this.entries.forEach(function (entry) {
        if (entry.available && entry.rank !== null && entry.rank > rank) {
          if (!best || entry.rank < best.rank) best = entry;
        }
      });
      return best;
    }

    /** Nearest head-circumference (cm) whose resolved size is actually available. */
    nearestAvailableCm(cm) {
      var best = null;
      var bestDistance = Infinity;
      for (var c = this.min; c <= this.max; c += 1) {
        var target = this.resolveTarget(c);
        if (target && target.available) {
          var distance = Math.abs(c - cm);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = c;
          }
        }
      }
      return best;
    }

    nearestAvailable(rank) {
      var best = null;
      var bestDistance = Infinity;
      this.entries.forEach(function (entry) {
        if (entry.available && entry.rank !== null) {
          var distance = Math.abs(entry.rank - rank);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = entry;
          }
        }
      });
      return best;
    }

    firstAvailable() {
      for (var i = 0; i < this.entries.length; i += 1) {
        if (this.entries[i].available) return this.entries[i];
      }
      return null;
    }

    apply() {
      if (!this.range) return;

      var cm = this.currentCm;
      var target = this.resolveTarget(cm);

      // Unavailable sizes can't be selected: snap the slider to the nearest available size.
      if (!target || !target.available) {
        var snapCm = this.nearestAvailableCm(cm);
        if (snapCm === null) return;
        this.range.value = snapCm;
        this.update();
        cm = snapCm;
        target = this.resolveTarget(cm);
        if (!target || !target.available) return;
      }

      var selector =
        'input[data-option-position="' +
        this.optionPosition +
        '"][form="' +
        this.formId +
        '"][value="' +
        target.id +
        '"]';

      var input = document.querySelector(selector);
      if (!input) return;

      if (!input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  window.customElements.define('hd-sizefinder', HdSizefinder);
})();
